import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Row, Col, Badge, Table, Alert, Spinner } from 'react-bootstrap';
import { FiArrowLeft, FiCheckCircle, FiCalendar, FiMapPin, FiCloud, FiAlertTriangle, FiEye, FiEyeOff } from 'react-icons/fi';
import 'bootstrap/dist/css/bootstrap.css';
import { getCheckClaimById } from '../../services/api';
import { handleApiError } from '../../utils/errorHandler';
import WeatherDataSections from '../../components/Common/WeatherDataSections';
import PolicySumInsuredRows from '../../components/Common/PolicySumInsuredFields';

const Label = ({ children }) => (
    <div style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{children}</div>
);

const Value = ({ children }) => (
    <div style={{ fontWeight: 600, color: 'var(--text-primary)' }}>{children}</div>
);

const Section = ({ title, children }) => (
    <Card className="mb-3">
        <Card.Header style={{ fontWeight: 600 }}>{title}</Card.Header>
        <Card.Body>{children}</Card.Body>
    </Card>
);

const AdminCheckClaimDetail = () => {
    const navigate = useNavigate();
    const { checkClaimId } = useParams();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [checkClaim, setCheckClaim] = useState(location.state?.checkClaim || null);
    const [showIban, setShowIban] = useState(false);

    useEffect(() => {
        const fetchCheckClaim = async () => {
            try {
                setLoading(true);
                setError(null);
                const data = await getCheckClaimById(checkClaimId);
                // API returns { check_claim: { ... } }
                setCheckClaim(data?.check_claim || null);
            } catch (e) {
                setError(handleApiError(e, 'Erreur lors du chargement du check claim'));
            } finally {
                setLoading(false);
            }
        };
        // Always fetch by id to ensure up-to-date data
        if (checkClaimId) fetchCheckClaim();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkClaimId]);

    if (loading) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du check claim</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3 text-muted">Chargement du check claim...</p>
                        </div>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du check claim</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <Alert variant="danger">
                            <FiAlertTriangle className="me-2" />
                            {error}
                        </Alert>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    if (!checkClaim) {
        return (
            <div className="dashboard-page">
                <Card>
                    <Card.Body>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                            <h2 className="page-title">Détail du check claim</h2>
                            <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                                <FiArrowLeft className="me-2" /> Retour
                            </Button>
                        </div>
                        <p className="text-muted">Aucune donnée de check claim disponible pour l'identifiant: {checkClaimId}.</p>
                    </Card.Body>
                </Card>
            </div>
        );
    }

    const get = (obj, path, fallback = '—') => {
        try {
            const v = path.reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
            return v ?? fallback;
        } catch {
            return fallback;
        }
    };

    const maskIban = (iban) => {
        if (!iban || iban === '—') return iban;

        // Retirer les espaces pour le traitement
        const cleanedIban = iban.replace(/\s/g, '');

        if (cleanedIban.length < 7) return iban; // IBAN trop court pour masquer

        // Garder les 4 premiers caractères (FR76)
        const prefix = cleanedIban.substring(0, 4);
        // Garder les 3 derniers caractères (185)
        const suffix = cleanedIban.substring(cleanedIban.length - 3);
        // Le milieu à masquer (tout sauf les 4 premiers et les 3 derniers)
        const middleLength = cleanedIban.length - 7;

        // Calculer le nombre de groupes de 4 caractères à masquer
        const numberOfGroups = Math.ceil(middleLength / 4);

        // Créer des groupes de 4 "xxxx" pour le milieu
        const maskedGroups = Array(numberOfGroups).fill('xxxx').join(' ');

        // Reconstruire avec espacement : FR76 xxxx xxxx xxxx xxxx xxxx 185
        return `${prefix} ${maskedGroups} ${suffix}`;
    };

    return (
        <div className="dashboard-page">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h2 className="page-title">
                    <FiCheckCircle className="me-2" />
                    Détail du check claim
                </h2>
                <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                    <FiArrowLeft className="me-2" /> Retour
                </Button>
            </div>

            <Section title="Informations principales">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label><FiCheckCircle className="me-1" />Forecast déclenché</Label>
                        <Value>
                            <Badge bg={get(checkClaim, ['is_triggered_forecast']) ? 'success' : 'secondary'}>
                                {get(checkClaim, ['is_triggered_forecast']) ? 'Oui' : 'Non'}
                            </Badge>
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Observation déclenchée</Label>
                        <Value>
                            {(() => {
                                // Essayer d'accéder au champ directement, puis dans CheckClaims si présent
                                let isTriggeredObs = checkClaim?.is_triggered_observation;

                                // Si pas trouvé directement, chercher dans CheckClaims
                                if (isTriggeredObs === undefined && checkClaim?.CheckClaims) {
                                    isTriggeredObs = checkClaim.CheckClaims.is_triggered_observation;
                                }

                                // Si toujours pas trouvé, utiliser la fonction get (qui retourne "—" si absent)
                                if (isTriggeredObs === undefined) {
                                    const isTriggeredObsRaw = get(checkClaim, ['is_triggered_observation']);
                                    isTriggeredObs = isTriggeredObsRaw === '—' ? null : isTriggeredObsRaw;
                                }

                                // Si is_triggered_observation est null ou undefined, afficher "En attente de traitement"
                                if (isTriggeredObs === null || isTriggeredObs === undefined) {
                                    return (
                                        <Badge bg="warning">
                                            En attente de traitement
                                        </Badge>
                                    );
                                }

                                // Si true, afficher "Oui", si false, afficher "Non"
                                return (
                                    <Badge bg={isTriggeredObs === true ? 'success' : 'secondary'}>
                                        {isTriggeredObs === true ? 'Oui' : 'Non'}
                                    </Badge>
                                );
                            })()}
                        </Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Forecast vérifié</Label>
                        <Value>
                            <Badge bg={get(checkClaim, ['forecast_checked']) === 'not created date' ? 'warning' : 'success'}>
                                {get(checkClaim, ['forecast_checked']) === 'not created date' ? 'Non vérifié' : get(checkClaim, ['forecast_checked'])}
                            </Badge>
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Observation vérifiée</Label>
                        <Value>
                            <Badge bg={get(checkClaim, ['observation_checked']) === 'not created date' ? 'warning' : 'success'}>
                                {get(checkClaim, ['observation_checked']) === 'not created date' ? 'Non vérifié' : get(checkClaim, ['observation_checked'])}
                            </Badge>
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Déclenchement cumulé</Label>
                        <Value>
                            <Badge bg={get(checkClaim, ['prcp_is_cumulated']) ? 'success' : 'secondary'}>
                                {get(checkClaim, ['prcp_is_cumulated']) ? 'Oui' : 'Non'}
                            </Badge>
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Dernière modification</Label>
                        <Value>{get(checkClaim, ['check_claim_last_modified_by'], 'N/A')}</Value>
                    </Col>
                </Row>
            </Section>

            <Section title="Informations d'activité">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Nom</Label>
                        <Value>{get(checkClaim, ['activity_info', 'name'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Catégorie</Label>
                        <Value>{get(checkClaim, ['activity_info', 'category'])}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label><FiCalendar className="me-1" />Date début</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_start_date'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Heure début</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_start_time'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label><FiCalendar className="me-1" />Date fin</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_end_date'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Heure fin</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_end_time'])}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label><FiMapPin className="me-1" />Adresse</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_address'])}</Value>
                    </Col>
                    <Col md={2}>
                        <Label>Code postal</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_zip_code'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Ville</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_city'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Pays</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_country'])}</Value>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Label>Prix</Label>
                        <Value>{get(checkClaim, ['activity_info', 'activity_price'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Durée</Label>
                        <Value>{get(checkClaim, ['activity_info', 'duration'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Code activité</Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {get(checkClaim, ['activity_info', 'activity_code'])}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Numéro réservation</Label>
                        <Value>{get(checkClaim, ['activity_info', 'reservation_number'])}</Value>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Label>Latitude</Label>
                        <Value>{get(checkClaim, ['activity_info', 'latitude'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Longitude</Label>
                        <Value>{get(checkClaim, ['activity_info', 'longitude'])}</Value>
                    </Col>
                </Row>
            </Section>

            <Section title="Informations principales du client">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Nom complet</Label>
                        <Value>{get(checkClaim, ['client_info', 'first_name'])} {get(checkClaim, ['client_info', 'last_name'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Email</Label>
                        <Value>{get(checkClaim, ['client_info', 'email'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Téléphone</Label>
                        <Value>{get(checkClaim, ['client_info', 'phone_number'])}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Code client</Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {get(checkClaim, ['client_info', 'client_code'])}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Statut</Label>
                        <Value>{get(checkClaim, ['client_info', 'status'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Date d'inscription</Label>
                        <Value>{get(checkClaim, ['client_info', 'date_joined'])}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={3}>
                        <Label>Civilité</Label>
                        <Value>{get(checkClaim, ['client_info', 'title'])}</Value>
                    </Col>
                    <Col md={3}>
                        <Label>Rôle</Label>
                        <Value>
                            {get(checkClaim, ['client_info', 'role']) !== '—' ? (
                                <Badge bg="info">{get(checkClaim, ['client_info', 'role'])}</Badge>
                            ) : (
                                '—'
                            )}
                        </Value>
                    </Col>
                    <Col md={3}>
                        <Label>Date de soumission</Label>
                        <Value>
                            {get(checkClaim, ['client_info', 'submission_date']) !== '—'
                                ? get(checkClaim, ['client_info', 'submission_date'])
                                : get(checkClaim, ['policy_info', 'date_rib_submission'])}
                        </Value>
                    </Col>
                    <Col md={3}>
                        <Label>Nom de l'entreprise (partenaire)</Label>
                        <Value>{get(checkClaim, ['partner_info', 'company_name'])}</Value>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Label>Adresse</Label>
                        <Value>{get(checkClaim, ['client_info', 'client_address'])}</Value>
                    </Col>
                    <Col md={2}>
                        <Label>Code postal</Label>
                        <Value>{get(checkClaim, ['client_info', 'zip_code'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Ville</Label>
                        <Value>{get(checkClaim, ['client_info', 'city'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Pays</Label>
                        <Value>{get(checkClaim, ['client_info', 'country'])}</Value>
                    </Col>
                </Row>
                {get(checkClaim, ['client_info', 'iban']) !== '—' && (
                    <Row className="mt-2">
                        <Col md={6}>
                            <Label>IBAN</Label>
                            <Value style={{ display: 'flex', alignItems: 'center', gap: '8px', fontFamily: 'monospace' }}>
                                {showIban
                                    ? get(checkClaim, ['client_info', 'iban'])
                                    : maskIban(get(checkClaim, ['client_info', 'iban']))
                                }
                                <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => setShowIban(!showIban)}
                                    style={{ padding: '0', minWidth: 'auto', color: 'var(--text-secondary)' }}
                                    title={showIban ? 'Masquer l\'IBAN' : 'Afficher l\'IBAN'}
                                >
                                    {showIban ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                                </Button>
                            </Value>
                        </Col>
                    </Row>
                )}
                {get(checkClaim, ['client_info', 'bic']) && get(checkClaim, ['client_info', 'bic']) !== '—' && (
                    <Row className="mt-2">
                        <Col md={6}>
                            <Label>BIC</Label>
                            <Value style={{ fontFamily: 'monospace' }}>
                                {get(checkClaim, ['client_info', 'bic'])}
                            </Value>
                        </Col>
                    </Row>
                )}
            </Section>

            <Section title="Informations de police">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Code police</Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {get(checkClaim, ['policy_info', 'policy_code'])}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Statut</Label>
                        <Value>{get(checkClaim, ['policy_info', 'status'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Date UW</Label>
                        <Value>{get(checkClaim, ['policy_info', 'uw_date'])}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Prix assurance (HT TSCA)</Label>
                        <Value>{get(checkClaim, ['policy_info', 'insurance_price_excl_tsca'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Prix TTC (incl. TSCA)</Label>
                        <Value>{get(checkClaim, ['policy_info', 'price_incl_tsca'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Montant TSCA</Label>
                        <Value>{get(checkClaim, ['policy_info', 'tsca_amount'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>TSCA (%)</Label>
                        <Value>{get(checkClaim, ['policy_info', 'tsca'])}</Value>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Label>Ratio Orakle</Label>
                        <Value>{get(checkClaim, ['policy_info', 'orakle_price_ratio_excl_tsca'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Langue préférée</Label>
                        <Value>{get(checkClaim, ['policy_info', 'preferred_language'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Date soumission RIB</Label>
                        <Value>{get(checkClaim, ['policy_info', 'date_rib_submission'])}</Value>
                    </Col>
                </Row>
                <PolicySumInsuredRows source={checkClaim?.policy_info} Label={Label} Value={Value} colMd={6} />
            </Section>

            <Section title="Informations de partenaire">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Nom</Label>
                        <Value>{get(checkClaim, ['partner_info', 'partner_first_name'])} {get(checkClaim, ['partner_info', 'partner_last_name'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Entreprise</Label>
                        <Value>{get(checkClaim, ['partner_info', 'company_name'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Email</Label>
                        <Value>{get(checkClaim, ['partner_info', 'email'])}</Value>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Label>Code</Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {get(checkClaim, ['partner_info', 'code'])}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Téléphone</Label>
                        <Value>{get(checkClaim, ['partner_info', 'phone_number'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Commission</Label>
                        <Value>{get(checkClaim, ['partner_info', 'commission'])}</Value>
                    </Col>
                </Row>
            </Section>

            <Section title="Informations de référence">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Nom référence</Label>
                        <Value>{get(checkClaim, ['reference_info', 'reference_name'])}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Catégorie</Label>
                        <Value>{get(checkClaim, ['reference_info', 'reference_category'])}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Code référence</Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {get(checkClaim, ['reference_info', 'reference_code'])}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Déclenchement cumulé</Label>
                        <Value>
                            <Badge bg={get(checkClaim, ['reference_info', 'prcp_is_cumulated']) ? 'success' : 'secondary'}>
                                {get(checkClaim, ['reference_info', 'prcp_is_cumulated']) ? 'Oui' : 'Non'}
                            </Badge>
                        </Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Trigger Low</Label>
                        <Value>{get(checkClaim, ['trigger_low'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Trigger Sup</Label>
                        <Value>{get(checkClaim, ['trigger_sup'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Trigger Hour Inf</Label>
                        <Value>{get(checkClaim, ['prcp_trigger_hour_inf'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Trigger Hour Sup</Label>
                        <Value>{get(checkClaim, ['trigger_hour_sup'], 'N/A')}</Value>
                    </Col>
                </Row>
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Trigger Number Hours</Label>
                        <Value>{get(checkClaim, ['trigger_number_hours'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Count Hour Triggered</Label>
                        <Value>{get(checkClaim, ['count_hour_triggered'], 'N/A')}</Value>
                    </Col>
                </Row>
                {checkClaim?.reference_info?.SubCoverage && Array.isArray(checkClaim.reference_info.SubCoverage) && checkClaim.reference_info.SubCoverage.length > 0 && (
                    <div className="mt-3">
                        <Label>Sous-couvertures</Label>
                        <Table striped bordered hover size="sm" className="mt-2">
                            <thead>
                                <tr>
                                    <th>Type déclenchement</th>
                                    <th>Trigger Low</th>
                                    <th>Trigger Sup</th>
                                    <th>Unité</th>
                                    <th>Ville</th>
                                    <th>Pays</th>
                                </tr>
                            </thead>
                            <tbody>
                                {checkClaim.reference_info.SubCoverage.map((sub, idx) => (
                                    <tr key={sub.sub_coverage_id || idx}>
                                        <td>{get(sub, ['trigger_type'])}</td>
                                        <td>{get(sub, ['trigger_low'])}</td>
                                        <td>{get(sub, ['trigger_sup'])}</td>
                                        <td>{get(sub, ['unit'])}</td>
                                        <td>{get(sub, ['city'])}</td>
                                        <td>{get(sub, ['country'])}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Section>

            {/* Informations prévision */}
            {checkClaim?.forecast_info && checkClaim.forecast_info.forecast_id && (
                <Section title="Informations prévision">
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Source de données</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'data_source'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label><FiCloud className="me-1" />Total mm précipitation</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'total_mm_prc'])}</Value>
                        </Col>
                    </Row>
                    <Row className="mb-2">
                        <Col md={4}>
                            <Label>Coordonnées</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'latitude'])}, {get(checkClaim, ['forecast_info', 'longitude'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Date init</Label>
                            <Value>
                                {get(checkClaim, ['forecast_info', 'meteomatics_init_day'])}/
                                {get(checkClaim, ['forecast_info', 'meteomatics_init_month'])}/
                                {get(checkClaim, ['forecast_info', 'meteomatics_init_year'])}
                            </Value>
                        </Col>
                        <Col md={4}>
                            <Label>Heure exécution (UTC)</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'meteomatics_run_hour_utc'])}</Value>
                        </Col>
                    </Row>
                    <Row>
                        <Col md={4}>
                            <Label>Dernière modification</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'forecast_last_modified_at'])}</Value>
                        </Col>
                        <Col md={4}>
                            <Label>Modifié par</Label>
                            <Value>{get(checkClaim, ['forecast_info', 'forecast_last_modified_by'])}</Value>
                        </Col>
                    </Row>
                    <WeatherDataSections
                        weatherData={checkClaim.forecast_info.weather_data}
                        weatherTemp={checkClaim.forecast_info.weather_temp}
                        weatherWind={checkClaim.forecast_info.weather_wind}
                        enableCopy
                        tableSize="sm"
                        bordered
                        className="mt-3"
                        idPrefix="check-claim"
                    />
                </Section>
            )}

            <Section title="Observations">
                <Row className="mb-2">
                    <Col md={4}>
                        <Label>Dernière observation ID</Label>
                        <Value style={{ fontFamily: 'monospace', fontSize: '0.9rem' }}>
                            {get(checkClaim, ['last_observation_info', 'last_observation_id'], 'N/A')}
                        </Value>
                    </Col>
                    <Col md={4}>
                        <Label>Source de données</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_data_source'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Total mm précipitation</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_total_mm_prc'], 'N/A')}</Value>
                    </Col>
                </Row>
                <Row>
                    <Col md={4}>
                        <Label>Station</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_station_name'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>ID Station</Label>
                        <Value>{get(checkClaim, ['last_observation_info', 'last_station_id'], 'N/A')}</Value>
                    </Col>
                    <Col md={4}>
                        <Label>Coordonnées station</Label>
                        <Value>
                            {get(checkClaim, ['last_observation_info', 'last_latitude_station'], 'N/A')}, {get(checkClaim, ['last_observation_info', 'last_longitude_station'], 'N/A')}
                        </Value>
                    </Col>
                </Row>
            </Section>
        </div>
    );
};

export default AdminCheckClaimDetail;

